// ============================================
// SUPABASE CLIENT
// ============================================
import { createClient } from '@supabase/supabase-js';

// These will be replaced with your actual Supabase project credentials
// For local development, create a .env file with these values
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';

// Check if Supabase is configured
export const isSupabaseConfigured = () => {
    return supabaseUrl && supabaseAnonKey;
};

// Create client (will be null if not configured)
export const supabase = isSupabaseConfigured()
    ? createClient(supabaseUrl, supabaseAnonKey)
    : null;

// ============================================
// AUTH FUNCTIONS
// ============================================

/**
 * Sign up a new user with email and password
 */
export async function signUp(email, password) {
    if (!supabase) throw new Error('Supabase not configured');

    const { data, error } = await supabase.auth.signUp({
        email,
        password,
    });

    if (error) throw error;
    return data;
}

/**
 * Sign in with email and password
 */
export async function signIn(email, password) {
    if (!supabase) throw new Error('Supabase not configured');

    const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
    });

    if (error) throw error;
    return data;
}

/**
 * Sign out the current user
 */
export async function signOut() {
    if (!supabase) throw new Error('Supabase not configured');

    const { error } = await supabase.auth.signOut();
    if (error) throw error;
}

/**
 * Get the current user
 */
export async function getCurrentUser() {
    if (!supabase) return null;

    const { data: { user } } = await supabase.auth.getUser();
    return user;
}

/**
 * Get the current session
 */
export async function getSession() {
    if (!supabase) return null;

    const { data: { session } } = await supabase.auth.getSession();
    return session;
}

/**
 * Listen for auth state changes
 */
export function onAuthStateChange(callback) {
    if (!supabase) return { data: { subscription: { unsubscribe: () => {} } } };

    return supabase.auth.onAuthStateChange((event, session) => {
        callback(event, session);
    });
}

// ============================================
// PURCHASE FUNCTIONS
// ============================================

/**
 * Send a one-time verification code to an email address.
 * Used by the restore-purchase flow to prove the requester owns the email
 * before any purchase information is revealed or Pro is unlocked.
 * A code is sent whether or not a purchase exists, so this endpoint
 * cannot be used to enumerate customer emails.
 */
export async function sendRestoreCode(email) {
    if (!supabase) throw new Error('Supabase not configured');

    const { error } = await supabase.auth.signInWithOtp({
        email,
        options: { shouldCreateUser: true },
    });

    if (error) {
        // Surface rate-limit errors honestly, keep everything else generic
        if (error.status === 429) {
            throw new Error('Too many attempts. Please wait a few minutes and try again.');
        }
        throw new Error('Could not send a verification code. Please try again.');
    }
}

/**
 * Verify the one-time code the user received by email.
 * On success the user has an authenticated session for that email.
 */
export async function verifyRestoreCode(email, token) {
    if (!supabase) throw new Error('Supabase not configured');

    const { data, error } = await supabase.auth.verifyOtp({
        email,
        token,
        type: 'email',
    });

    if (error) {
        throw new Error('That code is invalid or has expired. Please check it and try again.');
    }

    return data;
}

/**
 * Check if current user has Pro status (requires authenticated session)
 */
export async function checkUserProStatus() {
    const user = await getCurrentUser();
    if (!user) return false;

    const status = await verifyProStatusSecure();
    return status.isPro;
}

/**
 * Verify Pro status via Edge Function (more secure)
 * Requires an authenticated session - the edge function derives the
 * email/user from the session JWT, so the client cannot ask about
 * arbitrary emails.
 */
export async function verifyProStatusSecure() {
    if (!supabase) return { isPro: false, verified: false };

    try {
        const session = await getSession();
        if (!session) {
            return { isPro: false, verified: false, error: 'Not signed in' };
        }

        const { data, error } = await supabase.functions.invoke('verify-pro-status', {
            body: {},
        });

        if (error) {
            console.error('Pro verification error:', error);
            return { isPro: false, verified: false, error: error.message };
        }

        return {
            isPro: data.isPro,
            verified: true,
            purchaseDate: data.purchaseDate,
            planType: data.planType,
            expiresAt: data.expiresAt,
        };
    } catch (error) {
        console.error('Pro verification failed:', error);
        return { isPro: false, verified: false };
    }
}

// ============================================
// SAVED PROJECTS FUNCTIONS
// ============================================

/**
 * Get all saved projects for the current user
 */
export async function getSavedProjects() {
    if (!supabase) return [];

    const user = await getCurrentUser();
    if (!user) return [];

    const { data, error } = await supabase
        .from('saved_projects')
        .select('*')
        .eq('user_id', user.id)
        .order('slot', { ascending: true });

    if (error) {
        console.error('Error fetching saved projects:', error);
        return [];
    }

    return data || [];
}

/**
 * Save a project to a slot
 */
export async function saveProject(slot, projectData) {
    if (!supabase) throw new Error('Supabase not configured');

    const user = await getCurrentUser();
    if (!user) throw new Error('Must be logged in to save projects');

    const { data, error } = await supabase
        .from('saved_projects')
        .upsert({
            user_id: user.id,
            slot,
            name: projectData.name,
            container_id: projectData.containerId,
            container_name: projectData.containerName,
            item_count: projectData.itemCount,
            data: projectData,
        }, {
            onConflict: 'user_id,slot'
        })
        .select()
        .single();

    if (error) throw error;
    return data;
}

/**
 * Load a project from a slot
 */
export async function loadProject(slot) {
    if (!supabase) throw new Error('Supabase not configured');

    const user = await getCurrentUser();
    if (!user) throw new Error('Must be logged in to load projects');

    const { data, error } = await supabase
        .from('saved_projects')
        .select('*')
        .eq('user_id', user.id)
        .eq('slot', slot)
        .single();

    if (error) throw error;
    return data;
}

/**
 * Delete a saved project
 */
export async function deleteProject(slot) {
    if (!supabase) throw new Error('Supabase not configured');

    const user = await getCurrentUser();
    if (!user) throw new Error('Must be logged in to delete projects');

    const { error } = await supabase
        .from('saved_projects')
        .delete()
        .eq('user_id', user.id)
        .eq('slot', slot);

    if (error) throw error;
}

// ============================================
// PAYMENT FUNCTIONS
// ============================================

/**
 * Create a payment intent for Pro upgrade
 */
export async function createPaymentIntent(email) {
    if (!supabase) throw new Error('Supabase not configured');

    const { data, error } = await supabase.functions.invoke('create-payment-intent', {
        body: { email }
    });

    if (error) throw error;
    return data;
}

/**
 * Restore a purchase: verify the emailed code, then check Pro status
 * with the resulting authenticated session.
 */
export async function restorePurchase(email, token) {
    await verifyRestoreCode(email, token);
    const status = await verifyProStatusSecure();
    return status.isPro;
}
